import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Container,
  Paper,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { apiService } from '../services/apiService';

export default function ProjectSelector({ onProjectSelect, selectedProjectId, showInHeader = false }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const { user, isAdmin } = useAuth();
  const { setSelectedProject } = useProject();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      // If user is admin, get all projects; otherwise get user's assigned projects
      const data = isAdmin ? 
        await apiService.getAdminProjects() : 
        await apiService.getProjects();
      setProjects(data);
      
      // Auto-select first project if no project is selected
      if (data.length > 0 && !selectedProjectId) {
        onProjectSelect(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (event) => {
    const selectedId = event.target.value;
    const selectedProj = projects.find(p => p.id === selectedId);
    
    onProjectSelect(selectedId);
    setSelectedProject(selectedProj);
  };

  // Header mode - compact dropdown for app bar
  if (showInHeader) {
    if (loading) {
      return <CircularProgress size={20} sx={{ color: 'white' }} />;
    }

    if (error || projects.length === 0) {
      return (
        <Typography variant="body2" color="inherit" sx={{ opacity: 0.7 }}>
          {error || 'No Projects'}
        </Typography>
      );
    }

    return (
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <Select
          value={selectedProjectId || ''}
          onChange={handleProjectChange}
          displayEmpty
          sx={{
            color: 'white',
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.23)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            '.MuiSvgIcon-root ': {
              fill: 'white !important',
            }
          }}
        >
          {projects.length === 0 ? (
            <MenuItem value="">
              <em>No Projects Available</em>
            </MenuItem>
          ) : (
            projects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                <Typography>{project.name}</Typography>
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    );
  }

  // Original full-page mode
  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (projects.length === 0) {
    return (
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Projects Available
          </Typography>
          <Typography color="text.secondary">
            {isAdmin 
              ? 'You can create projects in the admin panel.' 
              : 'Please contact an administrator to get access to projects.'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper sx={{ p: 3, mt: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            Welcome, {user?.username}
          </Typography>
          {isAdmin && (
            <Chip 
              label="Admin" 
              color="primary" 
              size="small" 
            />
          )}
        </Box>
        
        <FormControl fullWidth>
          <InputLabel id="project-select-label">Select Project</InputLabel>
          <Select
            labelId="project-select-label"
            value={selectedProjectId || ''}
            label="Select Project"
            onChange={handleProjectChange}
          >
            {projects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body1">
                    {project.name}
                  </Typography>
                  {project.description && (
                    <Typography variant="body2" color="text.secondary">
                      {project.description}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isAdmin && (
          <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
            As an admin, you have access to all projects.
          </Typography>
        )}
      </Paper>
    </Container>
  );
}